import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { useQueryParams } from 'use-query-params';

import {
  FolderType,
  GetOrganizationItemsLocationType,
  GetOrganizationItemsParams,
  useGetOrganizationBySlug,
  useGetOrganizationItemsInfinite,
  useGetOrganizationStructure
} from '@/api/generated';
import DataGrid from '@/components/DataGrid/DataGrid';
import EmptyState from '@/components/EmptyState';
import { buildFlatStructure } from '@/components/fields/Location/locationTreeUtils';
import Form from '@/components/Form/Form';
import { useEntityList } from '@/hooks/useEntityList';
import { usePageTitle } from '@/hooks/usePageTitle';
import typography from '@/theme/typography';
import CreateOrganizationItemModal from '@/views/CreateOrganizationItemModal/CreateOrganizationItemModal';

import { DEFAULT_VALUES, GetOrganizationItemsRequestParams, URL_CONFIG_MAP } from './defaults';
import NewItemButton from './NewItemButton';
import { organizationItemColumns } from './organizationItemColumns';

const EMPTY_STATE_KEYS: Partial<Record<FolderType, string>> = {
  [FolderType.ORGANIZATION]: 'layouts.organizations.items.emptyOrg',
  [FolderType.SUB_ORGANIZATION]: 'layouts.organizations.items.emptySuborg',
  [FolderType.SUB_FOLDER]: 'layouts.organizations.items.emptyFolder',
  [FolderType.FOLDER]: 'layouts.organizations.items.emptyFolder'
};

const OrganizationItems = () => {
  const { t } = useTranslation('organizations');

  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);
  const { organizationSlug } = useParams() as {
    organizationSlug: string;
  };
  const [queryBody, setQueryBody] = useState<GetOrganizationItemsRequestParams | null>(null);
  const { data: organizationStructure } = useGetOrganizationStructure(organizationSlug!, {
    query: {
      enabled: !!organizationSlug
    }
  });
  const { data: organizationData } = useGetOrganizationBySlug(
    organizationSlug!,
    { isFull: true },
    { query: { enabled: !!organizationSlug } }
  );
  const isEnterprise = organizationData?.isEnterprise;

  const locationType = useMemo(() => {
    if (!organizationStructure) return null;
    const searchLocationSlug = locationSlug || organizationSlug;

    const items = buildFlatStructure(organizationStructure);
    const currentLocation = items?.find(({ slug: currentLocationSlug }) => searchLocationSlug === currentLocationSlug);

    if (!currentLocation) return null;

    return currentLocation.type as GetOrganizationItemsLocationType;
  }, [locationSlug, organizationSlug, organizationStructure]);

  const {
    data: organizationItemsData,
    isError,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetOrganizationItemsInfinite(organizationSlug, queryBody as GetOrganizationItemsParams, {
    query: { enabled: !!queryBody, suspense: false }
  });

  const handleFetchNextPage = useCallback(() => {
    if (isError || isFetchingNextPage || !hasNextPage || !organizationItemsData?.pages?.length) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage, organizationItemsData?.pages?.length]);

  const organizationItems = useMemo(
    () => (organizationItemsData?.pages || []).flatMap(page => page?.organizationItemDtos || []),
    [organizationItemsData]
  );

  const buildQueryBody = useCallback(
    (formValues: GetOrganizationItemsRequestParams) =>
      (locationType && {
        ...formValues,
        locationSlug: locationSlug || organizationSlug,
        locationType: locationType
      }) as GetOrganizationItemsRequestParams,
    [locationSlug, locationType, organizationSlug]
  );

  usePageTitle(t('layouts.organizations.items.title'));

  const { sorting, handleChangeSort, form } = useEntityList(
    DEFAULT_VALUES,
    URL_CONFIG_MAP,
    setQueryBody,
    buildQueryBody,
    300
  );

  const [activeModalItemType, setActiveModalItemType] = useState<FolderType | null>(null);
  const [draftItemSlug, setDraftItemSlug] = useState<string | null>(null);

  const handleCloseModal = useCallback(() => {
    setActiveModalItemType(null);
    setDraftItemSlug(null);
  }, []);

  const handleEditDraftItem = useCallback((draftItemSlug: string, type: FolderType) => {
    setActiveModalItemType(type);
    setDraftItemSlug(draftItemSlug);
  }, []);

  const handleOpenCreateModal = useCallback((type: FolderType) => {
    setActiveModalItemType(type);
  }, []);

  const columns = useMemo(
    () => organizationItemColumns(t, organizationSlug, handleEditDraftItem),
    [t, organizationSlug, handleEditDraftItem]
  );

  const emptyStateComponent = useMemo(() => {
    if (!locationType) return null;

    const translationKey = EMPTY_STATE_KEYS[locationType];
    if (!translationKey) return null;

    return (
      <EmptyState imageUrl="/images/empty-state-generic.svg" imageAlt={t(translationKey)}>
        <Typography
          variant="body2"
          sx={{ textAlign: 'center', fontWeight: typography.fontWeightMedium, color: 'grey.700' }}
        >
          {t(translationKey)}
        </Typography>
      </EmptyState>
    );
  }, [t, locationType]);

  return (
    <Box sx={{ px: 5, pb: 5, flexGrow: 1 }} component="div">
      <Form className="Filters-root" form={form}>
        <Stack direction="row" justifyContent="end" mt={2}>
          <Stack direction="row" gap={2}>
            {/* <FilterButton isOpen={showFilters} onClick={() => setShowFilters(true)} /> */}
            <NewItemButton
              queryBody={queryBody}
              locationSlug={locationSlug || organizationSlug}
              organizationSlug={organizationSlug}
              isEnterprise={isEnterprise}
              locationType={locationType ?? undefined}
              onOpenModal={handleOpenCreateModal}
            />
            {activeModalItemType && (
              <CreateOrganizationItemModal
                onClose={handleCloseModal}
                currentFilter={queryBody}
                type={activeModalItemType}
                locationSlug={locationSlug}
                organizationSlug={organizationSlug}
                draftItemSlug={draftItemSlug}
              />
            )}
          </Stack>
        </Stack>
      </Form>

      <DataGrid
        data={organizationItems}
        columns={columns}
        onSort={handleChangeSort}
        isLoading={isLoading || !queryBody}
        isLoadingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        sorting={sorting}
        resizeWatchSelector=".Filters-root"
        hasError={isError}
        onLoadNextPage={handleFetchNextPage}
        emptyStateComponent={emptyStateComponent}
      />
    </Box>
  );
};

export default OrganizationItems;
